import type { Datalayer, DatalayerInputEvent } from '@analytics/models';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ConsentService } from '@core/consent';
import { bufferIfOp } from '@innogy/utils-rxjs';
import { map, startWith, type Observable } from 'rxjs';

import { SideEffectsManager } from '../side-effects/side-effects-manager.service';
import { DatalayerEventFilter } from './event-filter.service';
import { DatalayerEventStreamEnhancer } from './event-stream-enhancer.service';
import { DatalayerOutputManager } from './output-manager.service';

/**
 * Service responsible for processing data layer events, applying enhancements, and dispatching them to configured outputs.
 */
@Injectable({
  providedIn: 'root',
})
export class DatalayerEventProcessor {
  // Injecting required dependencies for analytics processing and metadata retrieval.
  readonly #filter = inject(DatalayerEventFilter);
  readonly #outputManager = inject(DatalayerOutputManager);
  readonly #eventEnhancer = inject(DatalayerEventStreamEnhancer);
  readonly #sideEffects = inject(SideEffectsManager);
  readonly #destroyRef = inject(DestroyRef);
  readonly #consentService = inject(ConsentService);
  readonly #shouldBufferEvents$ = toObservable(
    this.#consentService.consentStatus
  ).pipe(
    startWith(true),
    map((value) => !value)
  );

  /**
   * Buffers incoming events until consent is granted by the user
   * to start processing analytics events.
   */
  #bufferEventsUntilConsentGranted() {
    return (source: Observable<DatalayerInputEvent>) =>
      source.pipe(bufferIfOp(this.#shouldBufferEvents$));
  }

  /**
   * Starts the processing of analytics events.
   * - Subscribes to enhanced event streams.
   * - Buffers all incoming events until consent is granted by the end-user.
   * - Applies all side-effects onto the event stream.
   * - Applies filtering tags and dispatches events to outputs.
   * - Cleans up resources on component destruction.
   */
  startProcessingEvents(datalayer: Datalayer) {
    // Subscribe to enhanced events, applying filters and dispatching to outputs.
    this.#eventEnhancer
      .eventStreamAsGDDLEvents(datalayer)
      .pipe(
        this.#bufferEventsUntilConsentGranted(),
        this.#sideEffects.applySideEffects(datalayer),
        map((event) => this.#filter.applyFilteringTagsToEvent(event)), // Apply tags to events for the output dispatcher to act on.
        map((event) => this.#outputManager.dispatchEventToOutputs(event)), // Dispatch event to outputs.
        takeUntilDestroyed(this.#destroyRef) // Unsubscribe from the stream when the service is destroyed to avoid memory leaks.
      )
      .subscribe();
  }
}
